import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { usePaginatedApiRequest } from "../../hooks/usePaginatedApiRequest";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { DownloadIcon, MoreVerticalIcon } from "../../styles/icons";
import { useApiClient } from "../../hooks/useApiClient";
import { useToast } from "../../hooks/useToast";

interface AttendanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  eventConfigurationId: string;
  totalScans: string;
}

export const AttendanceDrawer = ({
  isOpen,
  onClose,
  businessId,
  eventConfigurationId,
  totalScans,
}: AttendanceDrawerProps) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const apiClient = useApiClient();
  const { showSuccessToast, showErrorToast } = useToast();

  // Trigger a refresh when the drawer opens
  useEffect(() => {
    if (isOpen) {
      setRefreshTrigger((prev) => prev + 1);
    }
  }, [isOpen]);

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const downloadCsv = async (includeInvalidScans: boolean) => {
    try {
      const result = await apiClient.downloadEventAttendanceAsCsv(
        businessId,
        eventConfigurationId,
        includeInvalidScans
      );

      if (result.ok) {
        const blobData = new Blob([result.data]);
        const url = URL.createObjectURL(blobData);
        const link = document.createElement("a");
        const fileName = `event-attendance-${eventConfigurationId}-${
          includeInvalidScans ? "all" : "valid"
        }.csv`;
        link.download = fileName;
        link.href = url;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showSuccessToast(
          "The attendance data has been downloaded successfully"
        );
      } else {
        showErrorToast("Failed to download the attendance data");
      }
    } catch (error) {
      showErrorToast("An error occurred while downloading attendance data");
      console.error("Download error:", error);
    }
  };

  const {
    items: attendanceRecords,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getEventAttendance(businessId, eventConfigurationId),
    // Request subsequent pages
    (apiClient, continuationToken) =>
      apiClient.getEventAttendance(
        businessId,
        eventConfigurationId,
        continuationToken
      ),
    [businessId, eventConfigurationId, refreshTrigger]
  );

  const drawerContentStyles = {
    maxWidth: "1000px",
    width: "100%",
  };

  if (!isOpen) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right">
      <DrawerOverlay />
      <DrawerContent sx={drawerContentStyles}>
        <Flex position="absolute" right="40px" top="8px" zIndex="1">
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Download options"
              icon={<MoreVerticalIcon />}
              variant="ghost"
              size="sm"
            />
            <MenuList>
              <MenuItem
                onClick={() => downloadCsv(false)}
                icon={<DownloadIcon />}
              >
                Export Only Valid Scans (CSV)
              </MenuItem>
              <MenuItem
                onClick={() => downloadCsv(true)}
                icon={<DownloadIcon />}
              >
                Export All Scans (CSV)
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        <DrawerCloseButton />
        <DrawerHeader>{`TOTAL CHECK-INS`}</DrawerHeader>
        <DrawerBody>
          <Box marginBottom={2}>{`Total: ${totalScans} Members`}</Box>

          {isLoading && !attendanceRecords && (
            <Center>
              <Spinner margin="4" />
            </Center>
          )}

          {error && (
            <ErrorMessage>
              An error occurred while fetching attendance data.
            </ErrorMessage>
          )}

          {attendanceRecords && attendanceRecords.length > 0 && (
            <>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Member Name</Th>
                    <Th>Membership Level</Th>
                    <Th>Check-in Time</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {attendanceRecords.map((attendance, index) => (
                    <Tr key={index}>
                      <Td>{`${attendance.Member?.FirstName} ${attendance.Member?.LastName}`}</Td>
                      <Td>
                        {attendance.Member?.MemberLevelDescription || "-"}
                      </Td>
                      <Td>{formatDateTime(attendance.ScanDateTime)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {fetchNextPage && (
                <Box mt={4} display="flex" justifyContent="center">
                  {!isLoadingPage && (
                    <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                      Load more...
                    </Button>
                  )}
                  {isLoadingPage && <Spinner />}
                </Box>
              )}
            </>
          )}

          {!isLoading &&
            (!attendanceRecords || attendanceRecords.length === 0) && (
              <Box textAlign="center" py={4}>
                No attendance records found.
              </Box>
            )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
